import React, { useMemo, useState } from "react";
import Modal from "src/components/Modal/Modal";
import { cloneDeep } from "lodash";
import { FiltersValue } from "./types";
import ToggleButton from "./ToggleButton";
import { ReactComponent as X } from "src/assets/svg/x5.svg";
import Button from "src/components/Button";
import CreatableDropdownSelect from "src/components/Form/DropdownSelect/CreatableDropdownSelect";
import { defaultFilters } from "./constant";
import DropdownSelect from "src/components/Form/DropdownSelect/DropdownSelect";
import SalaryRange from "src/components/Form/SalaryRange/SalaryRange";
import Job from "src/types/models/Job";
import Checkbox from "src/components/Form/Checkbox/Checkbox";
import { getCountryCode } from "countries-list";
import { State } from "country-state-city";

interface IFiltersModal {
  value: FiltersValue;
  setValue: (v: FiltersValue) => void;
  open: boolean;
  setOpen: (o: boolean) => void;
  jobs?: Job[];
}

const FiltersModal = ({ value, setValue, open, setOpen, jobs }: IFiltersModal) => {
  const [current, setCurrent] = useState<string>("Posted Job");
  const [filters, setFilters] = useState<FiltersValue>(cloneDeep(value));
  const [seed, setSeed] = useState(0);
  const setFilter = (key: keyof FiltersValue, v: any) => {
    setFilters({ ...filters, [key]: v });
  };
  const jobOptions = useMemo(() => jobs?.map((j) => ({ label: j.job_title, value: j.id })), [jobs]);

  // Methods
  const removeFromArray = (key: keyof FiltersValue, value: string) => {
    const arr = filters[key];
    if (!Array.isArray(arr)) {
      console.error("Not an array");
      return;
    }
    const _arr = arr.filter((a) => a.value !== value);
    setFilter(key, _arr);
  };
  const clearFilters = () => {
    // This will help us to reset the inputs by force
    setSeed(seed + 1);
    setFilters(defaultFilters());
  };
  const closeModal = () => setOpen(false);
  const applyFilters = () => {
    closeModal();
    setValue(filters);
  };

  const disabledState = filters.countries?.length !== 1;

  // Show only states for the selected
  const stateOptions = useMemo(() => {
    if (disabledState) {
      setFilter("state", undefined);
      return [];
    }
    const country = filters?.countries?.[0].value;
    const code = getCountryCode(country || "");
    const states = State.getStatesOfCountry(code || "");
    if (!states?.length) return [];
    return states.map((s) => ({ label: s.name, value: s.name }));
  }, [filters.countries]);

  return (
    <Modal
      key={seed}
      className="!w-fit  !p-0 flex flex-col"
      hideClose
      open={open}
      onChange={setOpen}
    >
      {/* Header */}
      <div className="flex justify-between items-center px-6 py-5 border-b-[1px] border-[#8a8a8a]">
        <p className="font-poppins text-lg font-medium">Filters</p>
        <X className="cursor-pointer" onClick={closeModal} />
      </div>
      <div className="flex overflow-hidden min-h-[620px]  ">
        {/* Left side */}
        <div className="p-6 flex flex-col gap-4 pb-[125px] w-[460px]  ">
          <ToggleButton
            value={filters.sort_by_job}
            current={current}
            setCurrent={setCurrent}
            removeFromOptions={() => setFilter("sort_by_job", undefined)}
            label="Posted Job"
            placeholder="Sort by Posted Job"
          />
          <ToggleButton
            value={filters.role}
            current={current}
            setCurrent={setCurrent}
            removeFromOptions={() => setFilter("role", undefined)}
            label="Role"
            placeholder="Select Role"
          />
          <ToggleButton
            value={filters.countries}
            current={current}
            setCurrent={setCurrent}
            removeFromOptions={removeFromArray}
            name="countries"
            label="Country"
            placeholder="Select Country"
          />
          <ToggleButton
            value={filters.state}
            disabled={disabledState}
            current={current}
            setCurrent={setCurrent}
            removeFromOptions={() => setFilter("state", undefined)}
            name="state"
            label="State"
            placeholder="Select State"
          />
          <ToggleButton
            value={filters.skills}
            current={current}
            setCurrent={setCurrent}
            removeFromOptions={removeFromArray}
            name="skills"
            label="Skills"
            placeholder="Select Skills"
          />
          <ToggleButton
            value={filters.resume_skills}
            current={current}
            setCurrent={setCurrent}
            removeFromOptions={removeFromArray}
            name="resume_skills"
            label="Resume Skills"
            placeholder="Select Skills"
          />
          <ToggleButton
            value={filters.experience_level}
            current={current}
            setCurrent={setCurrent}
            removeFromOptions={() => setFilter("experience_level", undefined)}
            label="Experience"
            placeholder="Select Experience Level"
          />
          <SalaryRange
            onChange={(s) => setFilter("salary", s)}
            showCurrency={false}
            value={filters.salary}
            label="Salary Range"
            labelClassName="!text-base !text-[#8A8A8A] !font-medium"
            input1={{ className: "!w-[177px]" }}
            input2={{ className: "!w-[177px]" }}
          />
          <Checkbox
            onChange={(b) => setFilter("vetted", b)}
            value={filters.vetted}
            title="Vetted"
            style={1}
          />
        </div>
        {/* Right side */}
        {current === "Posted Job" && (
          <Wrap label="Posted Job">
            <CreatableDropdownSelect
              {...selecProps}
              isDisabled={!jobOptions?.length}
              value={filters.sort_by_job}
              onChange={(o) => setFilter("sort_by_job", o)}
              options={jobOptions}
            />
          </Wrap>
        )}

        {current === "Role" && (
          <Wrap label="Role">
            <CreatableDropdownSelect
              {...selecProps}
              value={filters.role}
              onChange={(o) => setFilter("role", o)}
              _options="talent_roles"
              _createLabel="filtering"
            />
          </Wrap>
        )}
        {current === "Country" && (
          <Wrap label="Country">
            <CreatableDropdownSelect
              {...selecProps}
              value={filters.countries}
              _options="countries"
              isClearable={false}
              _createLabel="filtering"
              isMulti
              onChange={(o) => setFilter("countries", o)}
            />
          </Wrap>
        )}
        {current === "State" && (
          <Wrap label="State">
            <CreatableDropdownSelect
              {...selecProps}
              value={filters.state}
              options={stateOptions}
              isClearable={false}
              _createLabel="filtering"
              onChange={(o) => setFilter("state", o)}
            />
          </Wrap>
        )}
        {current === "Skills" && (
          <Wrap label="Skills">
            <CreatableDropdownSelect
              {...selecProps}
              value={filters.skills}
              _options="talent_skills"
              isClearable={false}
              _createLabel="filtering"
              isMulti
              onChange={(o) => setFilter("skills", o)}
            />
          </Wrap>
        )}
        {current === "Resume Skills" && (
          <Wrap label="Resume Skills">
            <CreatableDropdownSelect
              {...selecProps}
              value={filters.resume_skills}
              _options="talent_skills"
              isClearable={false}
              _createLabel="filtering"
              isMulti
              onChange={(o) => setFilter("resume_skills", o)}
            />
          </Wrap>
        )}
        {current === "Experience" && (
          <Wrap label="Experience">
            <DropdownSelect
              {...selecProps}
              value={filters.experience_level}
              _options="experience_levels"
              onChange={(o) => setFilter("experience_level", o)}
            />
          </Wrap>
        )}
      </div>
      {/* Footer */}
      <div className="flex  items-center px-6 py-5 border-t-[1px] border-[#8a8a8a] ">
        <button
          onClick={clearFilters}
          className="font-poppins text-base font-semibold text-odfblue underline"
        >
          Clear All
        </button>
        <Button onClick={closeModal} className="ml-auto !px-4 !w-fit" variant="white_blue">
          Cancel
        </Button>
        <Button onClick={applyFilters} className="ml-6 !px-4 !w-fit">
          Apply Filters
        </Button>
      </div>
    </Modal>
  );
};

// Props for this very spefic design of dropdown select
const selecProps: React.ComponentProps<typeof CreatableDropdownSelect> = {
  menuIsOpen: true,
  classNames: {
    menu: () => "!relative !border-0 !bg-transparent !shadow-none  ",
    menuList: () => "!max-h-[450px]"
  },
  components: {
    // MultiValue: () => null, // Do not show the picket multi options in the input
    Option: ({ innerRef, innerProps, isSelected, data, isMulti }) => {
      return (
        <div
          ref={innerRef}
          {...innerProps}
          className={`group flex items-center py-3 px-4 font-poppins font-medium mb-[10px] text-sm bg-white rounded-lg border-[1px] border-transparent  hover:border-odfblue hover:text-odfblue 
              ${isSelected && "text-odfblue !border-odfblue"}`}
        >
          {isMulti ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              className={`mr-4  rounded-lg ${!isSelected && "border-[1px]"} border-[#ccc] group-hover:border-odfblue  `}
              fill={isSelected ? "#5F5FFF" : "none"}
            >
              <path d="M16.19 2H7.81C4.17 2 2 4.17 2 7.81V16.18C2 19.83 4.17 22 7.81 22H16.18C19.82 22 21.99 19.83 21.99 16.19V7.81C22 4.17 19.83 2 16.19 2ZM16.78 9.7L11.11 15.37C10.97 15.51 10.78 15.59 10.58 15.59C10.38 15.59 10.19 15.51 10.05 15.37L7.22 12.54C6.93 12.25 6.93 11.77 7.22 11.48C7.51 11.19 7.99 11.19 8.28 11.48L10.58 13.78L15.72 8.64C16.01 8.35 16.49 8.35 16.78 8.64C17.07 8.93 17.07 9.4 16.78 9.7Z" />
            </svg>
          ) : (
            <div
              className={`p-1 mr-4 rounded-full border-[1px] border-[#ccc] group-hover:border-odfblue ${isSelected && "!border-odfblue"} `}
            >
              <div
                className={`rounded-full w-[10px] h-[10px] bg-none  ${isSelected && "!bg-odfblue"}`}
              />
            </div>
          )}
          {(data as any).label}
        </div>
      );
    }
  }
};

const Wrap = ({ label = "", children }) => (
  <div className="bg-[#F9F9F9] px-6 py-4 w-[450px]">
    <p className="text-base font-semibold">{label}</p>
    <div className="bg-[#ccc] h-[1px] w-full my-4" />
    {children}
  </div>
);

export default FiltersModal;
